import React, { useEffect } from "react";

const EFFECTIVE_DATE = "19 October 2026";
const CONTACT_EMAIL = "hello@metamonk.co";

const Section = ({ id, title, children }) => (
  <section id={id} className="mt-10">
    <h2 className="font-display font-semibold" style={{ color: "#1A202C", fontSize: 20, letterSpacing: "-0.01em" }}>
      {title}
    </h2>
    <div className="mt-3 space-y-3" style={{ color: "#4A5568", fontSize: 15, lineHeight: 1.7 }}>
      {children}
    </div>
  </section>
);

const List = ({ items }) => (
  <ul className="list-disc pl-5 space-y-1.5">
    {items.map((t) => (
      <li key={t}>{t}</li>
    ))}
  </ul>
);

const Mail = () => (
  <a href={`mailto:${CONTACT_EMAIL}`} className="underline" style={{ color: "#0F6E56" }}>
    {CONTACT_EMAIL}
  </a>
);

export const PrivacyPolicy = () => {
  useEffect(() => {
    const prev = document.title;
    document.title = "Privacy Policy — Meta Monk Ventures";
    // index.html's canonical points at the homepage; this page is its own URL.
    const canonical = document.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical?.getAttribute("href");
    canonical?.setAttribute("href", "https://metamonk.co/privacy");
    window.scrollTo(0, 0);
    return () => {
      document.title = prev;
      if (prevCanonical) canonical.setAttribute("href", prevCanonical);
    };
  }, []);

  return (
    <main className="bg-white" style={{ paddingTop: 64 }} data-testid="privacy-page">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16">
        <span
          className="inline-block rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wider"
          style={{ backgroundColor: "#E1F5EE", color: "#0F6E56" }}
        >
          Legal
        </span>
        <h1
          className="font-display font-bold mt-4"
          style={{ color: "#1A202C", fontSize: "clamp(30px, 5vw, 40px)", letterSpacing: "-0.02em" }}
        >
          Privacy Policy
        </h1>
        <p className="mt-2" style={{ color: "#718096", fontSize: 14 }}>
          Effective {EFFECTIVE_DATE}
        </p>

        <p className="mt-6" style={{ color: "#4A5568", fontSize: 15, lineHeight: 1.7 }}>
          This policy explains how Meta Monk Ventures ("we", "us") collects, uses, shares and protects
          personal data when you visit metamonk.co or use our services. We handle personal data in line with
          India's Digital Personal Data Protection Act, 2023 and the rules made under it.
        </p>

        <Section id="who-we-are" title="1. Who we are">
          <p>
            Meta Monk Ventures is a registered proprietorship based in Chennai, Tamil Nadu, India. We distribute
            and facilitate financial products and related services. These cover mutual funds, insurance, loans and
            technology solutions. We also sell finance-themed physical and printable products, such as financial
            planners, posters, stickers and children's piggy banks. Some services are not yet live, and we will offer each one only once the
            required registrations or partnerships are in place.
          </p>
          <p>
            For any privacy question, contact us at <Mail />.
          </p>
        </Section>

        <Section id="what-we-collect" title="2. Information we collect">
          <p><strong>Information you give us</strong></p>
          <List
            items={[
              "Contact details, such as your name, email address and phone number, when you sign up for updates, email us or ask for a service.",
              "When you become a client, the details that regulators and product providers require. These may include identity and address proof, PAN, date of birth, photograph, signature, bank account details, nominee details, income, occupation and investment experience.",
              "For insurance, the information an insurer needs to assess a proposal. This can include health and lifestyle details.",
              "For loans, the information a lender needs to assess an application. This can include employment, income, existing debts and credit information.",
              "When you buy a product from us, your name, delivery address, phone number, email and order details. Payments are handled by our payment provider. We do not see or store your full card or UPI credentials.",
            ]}
          />
          <p><strong>Information from our website tools</strong></p>
          <p>
            Our calculators run entirely in your browser. The values you enter are not sent to us or stored by us.
          </p>
          <p><strong>Information collected automatically</strong></p>
          <p>
            Like most websites, our hosting provider records basic technical data such as IP address, browser type
            and the time of a request. This is used to keep the site running securely. Our pages also load fonts
            from Google Fonts, which receives your IP address. We do not currently use analytics or advertising
            cookies.
          </p>
        </Section>

        <Section id="how-we-use" title="3. How we use your information">
          <List
            items={[
              "To respond to your enquiries and send the updates you asked for, such as launch notifications.",
              "To provide the service you request, including completing KYC and processing applications, transactions and service requests with product providers.",
              "To process, deliver and support your product orders, including returns, refunds and invoices.",
              "To meet legal and regulatory obligations, including KYC, anti-money-laundering and record-keeping requirements.",
              "To keep our website and systems secure and to prevent fraud.",
            ]}
          />
          <p>
            We process personal data on the basis of your consent, or where the law otherwise permits it, for example
            to comply with a legal obligation. You can withdraw consent at any time (see section 7). Withdrawing
            consent may mean we can no longer provide a service that depends on it.
          </p>
        </Section>

        <Section id="sharing" title="4. Who we share it with">
          <p>We do not sell your personal data. We share it only as needed to provide a service or meet the law, with:</p>
          <List
            items={[
              "Product providers you choose to apply with, such as asset management companies, insurers, banks, NBFCs and other lenders.",
              "Payment providers that process your payments, and courier or logistics partners that deliver your orders.",
              "Intermediaries that process these products, such as registrar and transfer agents, KYC registration agencies, the Central KYC Registry, and credit information companies.",
              "Service providers that help us run our business, such as hosting, email and document storage. They may only use the data to provide their service to us.",
              "Regulators, government authorities, courts or law enforcement, where the law requires it.",
            ]}
          />
          <p>
            Each product provider handles your data under its own privacy policy as well, so please read it before
            you apply.
          </p>
        </Section>

        <Section id="retention" title="5. How long we keep it">
          <p>
            We keep personal data only as long as needed for the purpose it was collected, or for longer where a
            law or regulator requires it. Records linked to financial transactions and KYC must be kept for set
            periods, and order and invoice records are kept for as long as tax laws require. Launch-update signups are kept until you unsubscribe or we stop sending those updates.
          </p>
        </Section>

        <Section id="security" title="6. How we protect it">
          <p>
            We use reasonable security measures. These include encrypted connections (HTTPS), access controls, and
            reputable service providers. No method of transmission or storage is completely secure. If a personal
            data breach occurs, we will notify affected people and the authorities as the law requires.
          </p>
        </Section>

        <Section id="your-rights" title="7. Your rights">
          <p>Under the DPDP Act, you can:</p>
          <List
            items={[
              "Ask for a summary of the personal data we hold about you and how it is processed.",
              "Ask us to correct, complete, update or erase your personal data, unless the law requires us to keep it.",
              "Withdraw consent you have given.",
              "Raise a grievance with us, and nominate another person to exercise your rights if you die or become incapacitated.",
            ]}
          />
          <p>
            To use any of these rights, or to unsubscribe from updates, email <Mail />. We aim to respond within 30
            days. If you are not satisfied with our response, you can complain to the Data Protection Board of
            India.
          </p>
        </Section>

        <Section id="children" title="8. Children">
          <p>
            Our services are meant for adults. We do not knowingly collect personal data from anyone under 18
            without verifiable consent from a parent or lawful guardian. For a minor's investment or policy, we
            deal with the parent or guardian.
          </p>
        </Section>

        <Section id="links" title="9. Other websites">
          <p>
            Our site links to other websites, such as our social media pages. We are not responsible for their
            privacy practices.
          </p>
        </Section>

        <Section id="changes" title="10. Changes to this policy">
          <p>
            We may update this policy as our services or the law change. We will post the new version on this page
            with a new effective date.
          </p>
        </Section>

        <Section id="grievances" title="11. Contact and grievances">
          <p>
            Grievance contact: Meta Monk Ventures, Chennai, Tamil Nadu, India — <Mail />
          </p>
        </Section>

        <div className="mt-12">
          <a
            href="/"
            className="inline-flex items-center rounded-lg font-semibold text-white"
            style={{ backgroundColor: "#1D9E75", fontSize: 14, padding: "12px 24px" }}
          >
            Back to home
          </a>
        </div>
      </div>
    </main>
  );
};
